import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { sortAccountingFields } from "shared";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export const Route = createFileRoute(
  "/_authed/_org/accounting/settings/fields",
)({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(
      trpc.accounting.getAccountingFields.queryOptions(),
    );
    queryClient.ensureQueryData(trpc.accounting.get.queryOptions());
  },
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const location = useLocation();

  const { data: fields } = useSuspenseQuery(
    trpc.accounting.getAccountingFields.queryOptions(),
  );
  const { data: accountingData } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );
  if (location.pathname !== "/accounting/settings/fields") {
    return <Outlet />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b pt-6 pb-2">
        <Button variant="text" className="m-0 mx-6 h-4 w-16 p-0" asChild>
          <Link to="/accounting/settings">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span>Back</span>
          </Link>
        </Button>
        <SheetHeader>
          <SheetTitle>Accounting Fields</SheetTitle>
          <SheetDescription>
            Automatically assign expense accounts based on a transaction's card,
            budget, or spend category
          </SheetDescription>
        </SheetHeader>
      </div>
      <div className="flex w-full flex-col items-center space-y-4 px-4">
        <div className="mt-4 flex w-full flex-col space-y-4">
          {sortAccountingFields(
            fields,
            accountingData?.type ?? "quickbooks",
          ).map((field) => (
            <Link
              key={field.id}
              to="/accounting/settings/fields/$id"
              params={{ id: field.id }}
            >
              <Card className="cursor-pointer transition-all duration-200 hover:border-primary/20 hover:bg-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {field.displayName || field.name}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
